/**
 * BTK Cabinet Thumbnail Bootstrap
 * ----------------------------------------------------------------
 * Standalone entry that spins up a HEADLESS instance of the planner's
 * 3D renderer + catalogue so storefront pages (kitchen-units listing,
 * unit detail) can use the same off-screen thumbnail rendering as the
 * planner does inside its Add Units popup.
 *
 * Loads via snippets/btk-cabinet-thumbnail-loader.liquid which:
 *   1. Injects the catalogue JSON (window.BTKKitchenCatalogue) via the
 *      shared planner catalogue-data snippet.
 *   2. Mounts the three.js import map (so `three` resolves to the CDN
 *      build the planner uses).
 *   3. Loads this module with type="module".
 *
 * Exposes a single global:
 *
 *   window.btkRenderCabinetThumbnail = async function(cab, opts) → dataURL|null
 *
 * - `cab` is a synthetic cabinet object - same shape buildCabinetGLB
 *   in the planner uses (unitTypeId, variantId, kitchenStyleId,
 *   widthMm, heightMm, depthMm, doorColourHandle, carcaseColourHandle,
 *   cutoutSchema, handleSpec, etc).
 * - `opts` accepts { width, height, framingDims } same as the
 *   planner-side call.
 * - Lazy-initialises the headless renderer on first call so the
 *   storefront pages don't pay the init cost until something actually
 *   asks for a thumbnail. The bootstrap module itself is ~3KB; the
 *   renderer module (200KB-ish) only parses on first invocation.
 *
 * The bootstrap is intentionally tiny + idempotent - calling
 * btkRenderCabinetThumbnail repeatedly reuses the single headless
 * renderer instance.
 */

// ─── Catalogue data (async, cached) ───────────────────────────────
// The ~779KB catalogue used to be injected inline on EVERY page (via
// the cart drawer in overlay-group), blocking server render and bloating
// the HTML. Instead we now fetch it ONCE per session from the layout-
// less /cart?view=catalogue endpoint, run it via an injected inline
// <script>, and cache it in sessionStorage. The engine-catalogue module
// reads window.BTKKitchenCatalogue at module-eval time, so we MUST
// populate the global BEFORE dynamic-importing the engine.
//
// On the full planner page the catalogue is still injected inline by the
// planner section, so ensureCatalogueData() finds the global already set
// and skips the fetch entirely.
// Fallback endpoint: builds the catalogue from ALL 46 unit-type-group
// collections. Measured 2026-08-12 that walk blows the render timeout - it
// returned 500 on every request, the fetch below threw, and NO 3D thumbnail
// rendered anywhere on the storefront. Only used when we can't resolve the
// shopper's style collection.
const CATALOGUE_URL = '/cart?view=catalogue';
// Bump this when the catalogue shape or engine changes so stale
// per-session snapshots are discarded.
// v9: unit types gained `carcaseColourHandles` (the colours a unit is actually
// made in). A session still holding a v8 snapshot has no such key on any unit,
// so every restricted unit reads as unrestricted and the listing keeps drawing
// the Larder Pantry in whatever carcase the shopper picked.
const CATALOGUE_CACHE_KEY = 'btk_catalogue_cache_v9';

// Preferred endpoint: the shopper's own kitchen-units-<style> collection, via
// templates/collection.catalogue.liquid. ONE collection instead of 46 - renders
// in ~1.0-1.8s instead of timing out.
//
// The style has to travel in the URL PATH: cart.attributes is EMPTY on ?view=
// endpoints (verified - /cart.js and /cart both see kitchen_style, but
// /cart?view=catalogue sees null), so Liquid cannot read it there.
function styleCollectionUrl() {
  try {
    // window.BTK_CATALOGUE_STYLE lets a PAGE say whose products it needs.
    // The listing gates its cards to the active buying style, so the buying
    // context is right there. The DETAIL page can show a product from ANY
    // style, and it is that product's variant we must be able to look up -
    // otherwise buildCabinetDefinition returns no widthMm and the render
    // silently bails. Colours still come from the buying style; only the
    // products feed is scoped.
    const ctx = JSON.parse(sessionStorage.getItem('btk_kitchen_context') || 'null');
    const style = window.BTK_CATALOGUE_STYLE || (ctx && ctx.style);
    if (!style) return null;
    // window.BTKStyleData is emitted by sections/header.liquid on every page
    // and carries { name, collectionHandle } per style.
    const handle = window.BTKStyleData
      && window.BTKStyleData[style]
      && window.BTKStyleData[style].collectionHandle;
    if (!handle) return null;
    return {
      url: '/collections/' + encodeURIComponent(handle) + '?view=catalogue',
      // Cache per style - the scoped catalogue holds only that style's
      // products, so a shared key would serve the wrong style's data after
      // a style switch.
      key: CATALOGUE_CACHE_KEY + ':' + style,
      style
    };
  } catch (_) {
    return null;
  }
}

let _handle = null;
let _handlePromise = null;
let _cataloguePromise = null;

function runCatalogueScript(jsText) {
  // Execute the `window.BTKKitchenCatalogue = {...}` assignment via an
  // injected inline <script>. Consistent with the theme's existing
  // inline scripts; avoids the MIME check that would block <script src>
  // against the text/html endpoint, and avoids eval / CSP unsafe-eval.
  const s = document.createElement('script');
  s.textContent = jsText;
  (document.head || document.documentElement).appendChild(s);
  s.remove();
}

// Merge the unit types Liquid cannot reach.
//
// Liquid caps at 250 metaobjects per type and `page` does not advance it (see
// btk-unit-types-storefront.js). `.values` orders by HANDLE, so the tail was
// `true-handleless-*`: those products LISTED on the kitchen-units pages but
// would not RENDER, because a thumbnail needs the unit type's GLB and cutout
// schema. The Storefront API has no such ceiling.
async function mergeUnitTypeTail() {
  const cat = window.BTKKitchenCatalogue;
  if (!cat) return;
  try {
    const { fetchUnitTypesFromStorefront } = await import('./btk-unit-types-storefront.js?v=6');
    const list = await fetchUnitTypesFromStorefront();
    const have = new Set((cat.unitTypes || []).map(u => u && u.id));
    const add = list.filter(u => u && u.id && !have.has(u.id));
    if (add.length) cat.unitTypes = (cat.unitTypes || []).concat(add);
  } catch (e) {
    console.warn('[BTK Thumbnail] unit-type tail merge failed - units past the '
                 + 'Liquid 250 cap will not render', e);
  }
}

async function ensureCatalogueData() {
  // The planner page fetches its catalogue in pieces and sets the global when
  // they are all in (sections/btk-kitchen-planner.liquid). Wait for THAT
  // rather than fetching a smaller one here, which would set the global first
  // and leave the planner with a thumbnails-only catalogue.
  if (window.BTKCatalogueReady) { try { await window.BTKCatalogueReady; } catch (_) {} }
  // Already present? (planner page, or a prior call this page.)
  if (window.BTKKitchenCatalogue) return window.BTKKitchenCatalogue;
  // Session cache - the catalogue is identical across pages, so fetch
  // it at most once per browsing session.
  const scoped = styleCollectionUrl();
  const url = scoped ? scoped.url : CATALOGUE_URL;
  const cacheKey = scoped ? scoped.key : CATALOGUE_CACHE_KEY;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      window.BTKKitchenCatalogue = JSON.parse(cached);
      return window.BTKKitchenCatalogue;
    }
  } catch (_) {}
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error('[BTK Thumbnail] catalogue fetch ' + res.status + ' from ' + url);
  runCatalogueScript(await res.text());
  if (!window.BTKKitchenCatalogue) {
    throw new Error('[BTK Thumbnail] catalogue endpoint did not set the global');
  }
  await mergeUnitTypeTail();
  try {
    sessionStorage.setItem(cacheKey, JSON.stringify(window.BTKKitchenCatalogue));
  } catch (_) {}
  return window.BTKKitchenCatalogue;
}

// Populate the global, then dynamic-import the engine (which reads it at
// eval) and surface it as window.btkThumbnailCatalogue - the global the
// page's thumbnail managers poll for in their whenReady() gate.
async function ensureCatalogue() {
  if (_cataloguePromise) return _cataloguePromise;
  _cataloguePromise = (async () => {
    await ensureCatalogueData();
    // Keep this ?v= in lockstep with every other importer of the engine
    // module - a mismatch loads it twice as separate instances.
    const { Catalogue } = await import('./btk-kitchen-planner-engine-catalogue.js?v=194');
    window.btkThumbnailCatalogue = Catalogue;
    return Catalogue;
  })();
  return _cataloguePromise;
}

// A tab keeps the catalogue it first fetched (sessionStorage above), so units
// or sizes pushed to Shopify later have no variant info in it - their cards
// kept the old picture when those pills were clicked (Arden, 2026-10-31).
// The listing calls this when a card's variant is missing: fetch the style's
// catalogue fresh ONCE per page, swap that style's products into the engine
// and the session cache, and resolve true so the card can draw again.
let _refreshPromise = null;
window.btkRefreshCatalogueProducts = function btkRefreshCatalogueProducts() {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    const scoped = styleCollectionUrl();
    if (!scoped) return false;
    const Catalogue = await ensureCatalogue();
    const res = await fetch(scoped.url, { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) return false;
    const old = window.BTKKitchenCatalogue;
    runCatalogueScript(await res.text());
    const fresh = window.BTKKitchenCatalogue;
    if (!fresh || fresh === old) { window.BTKKitchenCatalogue = old; return false; }
    await mergeUnitTypeTail();
    Catalogue.setProductsForStyle?.(scoped.style, fresh.products || []);
    try { sessionStorage.setItem(scoped.key, JSON.stringify(fresh)); } catch (_) {}
    return true;
  })().catch(e => { console.warn('[BTK Thumbnail] catalogue refresh failed', e); return false; });
  return _refreshPromise;
};

// The heavy 3D renderer (~412KB) + three.js are loaded LAZILY via a
// dynamic import on the FIRST actual thumbnail render - not at module
// parse time - so pages that never render a thumbnail never pay for it.
async function ensureHeadlessRenderer() {
  if (_handle) return _handle;
  // Cache the in-flight promise so concurrent first calls (e.g. several
  // listing cards entering the viewport at once) share ONE renderer
  // import + init rather than racing to load the 412KB module twice.
  if (_handlePromise) return _handlePromise;
  _handlePromise = (async () => {
    const Catalogue = await ensureCatalogue();
    // Keep this ?v= in lockstep with every other importer of the
    // renderer module - a version mismatch loads it twice as separate
    // module instances. See the cache-bust version-skew note.
    const { init3DRenderer } = await import('./btk-kitchen-planner-3d-renderer.js?v=1126');
    // headless=true skips DOM mounting, OrbitControls, animation loop,
    // raycasting and resize observation. The renderCabinetThumbnail
    // path inside the module is fully self-contained - it creates its
    // own offscreen WebGLRenderer and scene per call.
    _handle = init3DRenderer(null, {
      catalogue: Catalogue,
      headless:  true
    });
    return _handle;
  })();
  return _handlePromise;
}

window.btkRenderCabinetThumbnail = async function btkRenderCabinetThumbnail(cab, opts) {
  try {
    const h = await ensureHeadlessRenderer();
    if (!h || typeof h.renderCabinetThumbnail !== 'function') return null;
    return await h.renderCabinetThumbnail(cab, opts);
  } catch (e) {
    console.warn('[BTK Thumbnail] render failed', e);
    return null;
  }
};

// Live-animated thumbnails. Streams 60fps frames into the caller's
// <canvas> via drawImage, then resolves with the final-frame data URL
// so the caller can swap their canvas back to a plain <img>. Shares
// ONE persistent off-screen WebGLRenderer for the whole page - only
// one line can animate at a time, which matches the realistic
// interaction pattern. See animateCabinetThumbnail in the renderer
// module for the full opts contract.
window.btkAnimateCabinetThumbnail = async function btkAnimateCabinetThumbnail(cab, opts) {
  try {
    const h = await ensureHeadlessRenderer();
    if (!h || typeof h.animateCabinetThumbnail !== 'function') return null;
    return await h.animateCabinetThumbnail(cab, opts);
  } catch (e) {
    console.warn('[BTK Thumbnail] animate failed', e);
    return null;
  }
};

// Dimension (line-art) render - same 3/4 viewpoint as the colour
// thumbnail, drawn as a black hidden-line technical drawing on white
// with width / depth / height dimension lines. Powers the unit detail
// page's second "dimensions" gallery slide.
window.btkRenderCabinetDimensionThumbnail = async function btkRenderCabinetDimensionThumbnail(cab, opts) {
  try {
    const h = await ensureHeadlessRenderer();
    if (!h || typeof h.renderCabinetDimensionThumbnail !== 'function') return null;
    return await h.renderCabinetDimensionThumbnail(cab, opts);
  } catch (e) {
    console.warn('[BTK Thumbnail] dimension render failed', e);
    return null;
  }
};

// Static render at an arbitrary progress (0=closed, 1=open). Used to
// pre-cache the OPEN final-frame so the swing-then-rest hand-off
// lands on an instantly-available image. Shares the animator rig.
window.btkRenderCabinetThumbnailAt = async function btkRenderCabinetThumbnailAt(cab, opts) {
  try {
    const h = await ensureHeadlessRenderer();
    if (!h || typeof h.renderCabinetThumbnailAt !== 'function') return null;
    return await h.renderCabinetThumbnailAt(cab, opts);
  } catch (e) {
    console.warn('[BTK Thumbnail] renderAt failed', e);
    return null;
  }
};

// ─── Shared door-swing helper ─────────────────────────────────────
// Drives the IMG↔CANVAS swap + animator call from any thumbnail
// manager (cart line-items, listing cards, detail page). Caller
// owns the cab + per-element state; this helper handles the DOM
// dance so each manager doesn't reinvent it.
//
//   cab          - pre-built synthetic cab (caller builds via Catalogue)
//   targetEl     - current <img> or <canvas> in the DOM to animate
//   openTarget   - true to swing open, false to close
//   opts         - { width, height, framingDims, durationMs }
//
// Resolves with the final-frame data URL (caller can cache it).
window.btkThumbSwing = async function btkThumbSwing(cab, targetEl, openTarget, opts) {
  opts = opts || {};
  if (!cab || !targetEl) return null;
  if (typeof window.btkAnimateCabinetThumbnail !== 'function') return null;

  // displayW/H: the IMG's CURRENT on-screen visual size. Drives the
  // canvas's CSS sizing so the swap is visually invisible.
  // renderW/H: the WebGL render target resolution (opts override for
  // detail-page hi-res thumbs, else same as display). Can differ from
  // displayW/H without ruining the swap - canvas CSS scaling handles it.
  //
  // Why both rect AND computed-style: for IMGs styled with
  // position:absolute + width:100% (e.g. .img-fit on the detail
  // gallery slide), getBoundingClientRect() can momentarily report
  // 0×0 in certain layout states even though the computed CSS width
  // resolves to the correct pixel value. Falling back to opts.width
  // (640 for detail) gave us a smaller-than-IMG canvas, hence the
  // "smaller during swing, bigger after" pulse. computedStyle.width
  // always returns the resolved pixel value, so it's the more
  // reliable fallback before resorting to opts.
  const cs       = window.getComputedStyle(targetEl);
  const rect     = targetEl.getBoundingClientRect();
  const cssW     = parseFloat(cs.width)  || 0;
  const cssH     = parseFloat(cs.height) || 0;
  const displayW = Math.round(rect.width)  || Math.round(cssW) || opts.width  || 320;
  const displayH = Math.round(rect.height) || Math.round(cssH) || opts.height || 320;
  const renderW  = opts.width  || displayW;
  const renderH  = opts.height || displayH;

  // Snapshot the ORIGINAL IMG's full attribute identity. The post-
  // swing IMG gets rebuilt from this - NOT cloned from the canvas —
  // so the canvas's locked-pixel display size doesn't leak into the
  // final IMG and break its responsive class-based sizing (e.g.
  // .img-fit's width:100% on the detail page). On a rapid re-toggle
  // the target is already a canvas; recover the snapshot stashed
  // there during the previous swing.
  const origAttrs = (targetEl.tagName === 'CANVAS' && targetEl.__btkOrigAttrs)
    ? targetEl.__btkOrigAttrs
    : (function () {
        const m = {};
        for (const attr of targetEl.attributes) {
          if (attr.name === 'src' || attr.name === 'srcset') continue;
          m[attr.name] = attr.value;
        }
        return m;
      })();

  // Reuse canvas on rapid re-toggle; otherwise build one matching
  // the IMG's exact visual footprint.
  let canvas;
  if (targetEl.tagName === 'CANVAS') {
    canvas = targetEl;
  } else {
    canvas = document.createElement('canvas');
    // Apply the original IMG's attrs to the canvas EXCEPT HTML
    // width/height (those have <canvas>-specific backing-store
    // semantics - we set canvas.width/.height directly below).
    for (const name in origAttrs) {
      if (name === 'width' || name === 'height') continue;
      try { canvas.setAttribute(name, origAttrs[name]); } catch (_) {}
    }
    // Lock the canvas into the IMG's exact LAYOUT BOX by copying its
    // computed layout-relevant CSS. Necessary because CSS rules can
    // target `img` specifically (e.g. ".bku-card-media img { display:
    // block; margin: 0 auto; max-height: 200px; object-fit: contain }"
    // on the listing, or .img-fit's position:absolute on the detail
    // gallery slide) - when those rules stop applying because the
    // element is now a <canvas>, the canvas falls back to default
    // inline display, no centring margin, no max-height, no absolute
    // positioning → wrong layout box → content below shifts, image
    // looks smaller/larger than it should during the swing.
    //
    // Copying computed values inline forces the canvas to occupy the
    // EXACT same box the IMG did. After the swing the canvas is
    // replaced with a fresh IMG built from origAttrs (no computed
    // styles carried over) so responsive class-based rules take
    // back over for normal layout.
    const cs = window.getComputedStyle(targetEl);
    const layoutProps = [
      'display', 'position', 'top', 'right', 'bottom', 'left',
      'margin', 'maxWidth', 'maxHeight', 'minWidth', 'minHeight',
      'objectFit', 'objectPosition', 'verticalAlign', 'boxSizing'
    ];
    for (const prop of layoutProps) {
      const v = cs[prop];
      if (v) canvas.style[prop] = v;
    }
    canvas.style.width  = displayW + 'px';
    canvas.style.height = displayH + 'px';
    // Inherit parent's background so any transparent canvas regions
    // (during cab-load, or where the animator's frame is transparent)
    // blend into the surrounding card invisibly. This kills the
    // "white flash" - what was actually a transparent canvas seen
    // against the parent's #fff. Falls back to #fff explicitly when
    // parent is transparent (rare for our card layouts).
    let parentBg = '';
    if (targetEl.parentElement) {
      try { parentBg = window.getComputedStyle(targetEl.parentElement).backgroundColor || ''; }
      catch (_) {}
    }
    if (!parentBg || /rgba?\([^)]*,\s*0\s*\)$/i.test(parentBg)) parentBg = '#fff';
    canvas.style.backgroundColor = parentBg;
    // CSS background-image as a LAST RESORT under-layer if drawImage
    // seeding below silently fails (IMG.complete=false at click time
    // is possible right after a fresh src= set). The bg-image shows
    // the IMG content until the first animator frame paints over it.
    if (targetEl.src) {
      const safeSrc = targetEl.src.replace(/"/g, '%22');
      canvas.style.backgroundImage    = 'url("' + safeSrc + '")';
      canvas.style.backgroundSize     = 'contain';
      canvas.style.backgroundPosition = 'center';
      canvas.style.backgroundRepeat   = 'no-repeat';
    }
    // Backing-store resolution matches the animator's dpr-scaled
    // render target. CRITICAL that we pre-size at the SAME pixel
    // count the animator will want - setting canvas.width later in
    // the animator would WIPE the seeded content below.
    const dpr = window.devicePixelRatio || 1;
    canvas.width  = Math.round(renderW * dpr);
    canvas.height = Math.round(renderH * dpr);
    // Stash for the rapid-re-toggle path above.
    canvas.__btkOrigAttrs = origAttrs;
    // Seed first frame from the IMG's current pixels so the swap
    // doesn't flash a blank canvas while the animator's first frame
    // is loading.
    try { canvas.getContext('2d').drawImage(targetEl, 0, 0, canvas.width, canvas.height); }
    catch (_) {}
    targetEl.replaceWith(canvas);
  }

  let finalUrl = null;
  try {
    finalUrl = await window.btkAnimateCabinetThumbnail(cab, {
      width: renderW, height: renderH,
      framingDims: opts.framingDims || null,
      fromProgress: openTarget ? 0 : 1,
      toProgress:   openTarget ? 1 : 0,
      durationMs:   opts.durationMs || 340,
      targetCanvas: canvas
    });
  } catch (_) {}

  // Cancelled mid-flight (newer call superseded us): fall back to
  // whatever's currently drawn so we never leave a stuck blank canvas.
  if (!finalUrl) {
    try { finalUrl = canvas.toDataURL('image/png'); } catch (_) {}
  }
  if (!canvas.isConnected) return finalUrl;
  if (finalUrl) {
    // Rebuild a CLEAN IMG from the original attrs snapshot. This
    // restores the IMG's pre-click sizing rules (e.g. class-based
    // width:100% or HTML width="350" attrs) - canvas's locked pixel
    // styles do NOT carry through, so subsequent renders stay
    // responsive and the next click measures the correct rect.
    const img = document.createElement('img');
    for (const name in origAttrs) {
      try { img.setAttribute(name, origAttrs[name]); } catch (_) {}
    }
    img.src = finalUrl;
    canvas.replaceWith(img);
  }
  return finalUrl;
};

// Kick off the catalogue load now (async, non-blocking) so that
// window.btkThumbnailCatalogue becomes available for the page's
// thumbnail managers - which poll for it in their whenReady() gate to
// resolve a unit type / variant into a synthetic cab - without waiting
// for an actual render request. Failures are swallowed: thumbnails just
// stay as the static featured image, exactly as when the catalogue was
// absent before.
ensureCatalogue().catch(function (e) {
  console.warn('[BTK Thumbnail] catalogue load failed', e);
});
